import { ComparableBoatData, useGetActiveCarouselItem } from '@components/CompareBoats/hooks/useCompare';
import { Grid } from 'antd';
import { KeenSliderPlugin, useKeenSlider } from 'keen-slider/react';
import { FC, useEffect } from 'react';
import styles from '../CompareTable.module.scss';
import PanelDataItem from './PanelDataItem.component';

interface EngineSpecsPanelDataInterface {
  boatComparableData: ComparableBoatData[];
}

const MutationPlugin: KeenSliderPlugin = (slider) => {
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      slider.update();
    });
  });

  const config = { childList: true };

  slider.on('created', () => {
    observer.observe(slider.container, config);
  });
  slider.on('destroyed', () => {
    observer.disconnect();
  });
};

const EngineSpecsPanelData: FC<EngineSpecsPanelDataInterface> = ({ boatComparableData }) => {
  const activeCarouselItem = useGetActiveCarouselItem();

  const { md } = Grid.useBreakpoint();

  const [ref, instanceRef] = useKeenSlider<HTMLDivElement>(
    {
      drag: false,
      slides: {
        perView: 7,
      },
      breakpoints: {
        '(max-width: 1440px)': {
          slides: { perView: 6 },
        },
        '(max-width: 1200px)': {
          slides: { perView: 5 },
        },
        '(max-width: 1024px)': {
          slides: { perView: 4 },
        },
        '(max-width: 992px)': {
          slides: { perView: 3 },
        },
        '(max-width: 768px)': {
          slides: { perView: 2 },
        },
        '(max-width: 520px)': {
          slides: { perView: 2 },
        },
      },
    },
    [MutationPlugin],
  );

  useEffect(() => {
    instanceRef?.current?.moveToIdx(activeCarouselItem);
  }, [activeCarouselItem, instanceRef]);

  const engineSpecsList: { specLabel: string; specId: number; noDataSpec?: boolean }[] = [
    { specLabel: 'Engine A', specId: 71, noDataSpec: true },
    { specLabel: 'Maker', specId: 72 },
    { specLabel: 'Website', specId: 73 },
    { specLabel: 'Model', specId: 74 },
    { specLabel: 'Type', specId: 75 },
    { specLabel: 'Drive', specId: 76 },
    { specLabel: 'Fuel', specId: 77 },
    { specLabel: 'Power', specId: 95 },
    { specLabel: 'Engine B', specId: 96, noDataSpec: true },
    { specLabel: 'Maker', specId: 97 },
    { specLabel: 'Website', specId: 98 },
    { specLabel: 'Model', specId: 99 },
    { specLabel: 'Type', specId: 100 },
    { specLabel: 'Drive', specId: 101 },
    { specLabel: 'Fuel', specId: 102 },
    { specLabel: 'Power', specId: 103 },
  ];

  return (
    <div className={styles.specsPanelDataContainer}>
      <div style={md ? { width: 192 } : { width: '100%' }}>
        {engineSpecsList.map((mainSpecItem) => (
          <div key={mainSpecItem.specId}>
            <div className={mainSpecItem?.noDataSpec ? styles.noDataSpecsNamesStyles : styles.specsNamesStyles}>
              {mainSpecItem.specLabel}
            </div>
            {mainSpecItem?.specId === 72 &&
              !md &&
              boatComparableData?.map((boat) => (
                <PanelDataItem panelData={boat?.engines?.at(0)?.maker} panelDataType="text" key={boat?._id} />
              ))}
            {mainSpecItem?.specId === 73 &&
              !md &&
              boatComparableData?.map((boat) => (
                <PanelDataItem
                  panelData={boat?.engines?.at(0)?.website ?? undefined}
                  panelDataType="link"
                  key={boat?._id}
                />
              ))}
            {mainSpecItem?.specId === 74 &&
              !md &&
              boatComparableData?.map((boat) => (
                <PanelDataItem panelData={boat?.engines?.at(0)?.model} panelDataType="text" key={boat?._id} />
              ))}
            {mainSpecItem?.specId === 75 &&
              !md &&
              boatComparableData?.map((boat) => (
                <PanelDataItem panelData={boat?.engines?.at(0)?.type} panelDataType="text" key={boat?._id} />
              ))}
            {mainSpecItem?.specId === 76 &&
              !md &&
              boatComparableData?.map((boat) => (
                <PanelDataItem panelData={boat?.engines?.at(0)?.drive} panelDataType="text" key={boat?._id} />
              ))}
            {mainSpecItem?.specId === 77 &&
              !md &&
              boatComparableData?.map((boat) => (
                <PanelDataItem panelData={boat?.engines?.at(0)?.fuel} panelDataType="text" key={boat?._id} />
              ))}
          </div>
        ))}
      </div>

      {md && (
        <div ref={ref} className="keen-slider">
          {boatComparableData?.map((boat) => (
            <div key={boat._id} className="keen-slider__slide">
              <PanelDataItem panelData={''} panelDataType="empty" />
              <PanelDataItem panelData={boat?.engines?.at(0)?.maker} panelDataType="text" />
              <PanelDataItem panelData={boat?.engines?.at(0)?.website ?? undefined} panelDataType="link" />
              <PanelDataItem panelData={boat?.engines?.at(0)?.model} panelDataType="text" />
              <PanelDataItem panelData={boat?.engines?.at(0)?.type} panelDataType="text" />
              <PanelDataItem panelData={boat?.engines?.at(0)?.drive} panelDataType="text" />
              <PanelDataItem panelData={boat?.engines?.at(0)?.fuel} panelDataType="text" />
              <PanelDataItem
                panelData={boat?.engines?.at(0)?.power}
                panelDataUnit={boat?.engines?.at(0)?.powerType}
                panelDataType="withUnits"
              />

              <PanelDataItem panelData={''} panelDataType="empty" />
              <PanelDataItem panelData={boat?.engines?.at(1)?.maker} panelDataType="text" />
              <PanelDataItem panelData={boat?.engines?.at(1)?.website ?? undefined} panelDataType="link" />
              <PanelDataItem panelData={boat?.engines?.at(1)?.model} panelDataType="text" />
              <PanelDataItem panelData={boat?.engines?.at(1)?.type} panelDataType="text" />
              <PanelDataItem panelData={boat?.engines?.at(1)?.drive} panelDataType="text" />
              <PanelDataItem panelData={boat?.engines?.at(1)?.fuel} panelDataType="text" />
              <PanelDataItem
                panelData={boat?.engines?.at(1)?.power}
                panelDataUnit={boat?.engines?.at(1)?.powerType}
                panelDataType="withUnits"
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default EngineSpecsPanelData;
